import {
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react';
import acsoftwareLogo from '../assets/acsoftware-logo.svg';
import './components.css';

/**
 * Componenti condivisi ACSoftware (§20.5).
 *
 * Tutti usano i medesimi design token: nessuno definisce colori propri, così
 * sostituire i placeholder con i valori ufficiali resta un'operazione su un
 * file solo.
 */

export function AcsButton({
  variant = 'primary',
  block = false,
  children,
  className,
  ref,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary';
  block?: boolean;
  /** React 19 passa `ref` come prop normale: serve alla gestione del focus nei dialog. */
  ref?: Ref<HTMLButtonElement>;
}) {
  const classes = [
    'acs-button',
    `acs-button--${variant}`,
    block ? 'acs-button--block' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type="button" className={classes} ref={ref} {...rest}>
      {children}
    </button>
  );
}

/**
 * Campo di testo con label reale sempre associata.
 *
 * `label` non è opzionale: un placeholder al posto della label è un difetto di
 * accessibilità, non una scelta di stile (§20.6).
 */
export function AcsInput({
  label,
  error,
  optionalHint,
  id,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string | undefined;
  optionalHint?: string | undefined;
}) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;

  return (
    <div className="acs-field">
      <label className="acs-field__label" htmlFor={inputId}>
        {label}
        {optionalHint ? <span className="acs-field__optional"> — {optionalHint}</span> : null}
      </label>
      <input
        id={inputId}
        className="acs-input"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        {...rest}
      />
      {error ? (
        <span id={errorId} className="acs-field__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Checkbox.
 *
 * Volutamente priva di varianti visive: non esiste un modo di renderla più
 * appariscente quando rappresenta un consenso. L'enfasi differenziata fra
 * accettare e non accettare è esattamente ciò che §20.8 vieta.
 */
export function AcsCheckbox({
  label,
  id,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <label className="acs-checkbox" htmlFor={inputId}>
      <input id={inputId} type="checkbox" className="acs-checkbox__control" {...rest} />
      <span className="acs-checkbox__label">{label}</span>
    </label>
  );
}

export function AcsCard({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={['acs-card', className ?? ''].filter(Boolean).join(' ')}>{children}</div>;
}

/**
 * Badge di stato.
 *
 * Il tono colora, non informa: l'etichetta testuale è sempre presente e
 * sufficiente da sola. Un badge che si distinguesse solo per tinta sarebbe
 * illeggibile a chi non percepisce quel contrasto (§20.6).
 */
export function AcsStatusBadge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger';
}) {
  const classes = ['acs-badge', tone === 'neutral' ? '' : `acs-badge--${tone}`]
    .filter(Boolean)
    .join(' ');
  return <span className={classes}>{children}</span>;
}

/**
 * Logo ACSoftware.
 *
 * Usa l'asset ufficiale `LogoEsteso.svg` così com'è: §20.2 vieta di
 * ridisegnarlo, alterarne le proporzioni o cambiarne i colori. Il file è
 * servito dall'app, non da un dominio esterno (§17).
 *
 * L'altezza è l'unico parametro: la larghezza segue il rapporto d'aspetto
 * originale (206 × 40).
 */
export function AcsLogo({ height = 32 }: { height?: number }) {
  return (
    <img
      src={acsoftwareLogo}
      alt="ACSoftware"
      height={height}
      // Safe area minima attorno al marchio (§20.2).
      style={{ height, width: 'auto', display: 'block', padding: '0 var(--acs-space-2)' }}
    />
  );
}

export function AcsPageShell({
  header,
  children,
  centered = false,
}: {
  header?: ReactNode;
  children: ReactNode;
  centered?: boolean;
}) {
  return (
    <div className="acs-page">
      {header ? <header className="acs-page__header">{header}</header> : null}
      <main className={centered ? 'acs-page__centered' : 'acs-page__main'}>{children}</main>
    </div>
  );
}
